import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Text,
  Heading,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  List,
  ListItem,
  ListIcon,
  Divider,
  Code,
  IconButton,
  useToast,
  useColorModeValue,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepTitle,
  StepDescription,
  StepSeparator,
  useSteps,
} from '@chakra-ui/react';
import { 
  DownloadIcon, 
  CheckCircleIcon, 
  LockIcon, 
  CopyIcon,
} from '@chakra-ui/icons';
import { FaDocker, FaFileCode, FaServer } from 'react-icons/fa';
import { useAuth0 } from '@auth0/auth0-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const DOCKER_API_URL = process.env.REACT_APP_DOCKER_API_URL || 'https://docker.indexerglue.com';

const steps = [
  { title: 'Generate', description: 'Create your secure config' },
  { title: 'Download', description: 'Get the Docker bundle' },
  { title: 'Deploy', description: 'Run with docker-compose' },
];

function DockerDownload() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState(null);
  const { activeStep, setActiveStep } = useSteps({ index: 0, count: steps.length });
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.700');
  const codeBg = useColorModeValue('gray.100', 'gray.800');

  useEffect(() => {
    if (isAuthenticated) {
      checkStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const checkStatus = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${DOCKER_API_URL}/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      console.error('Failed to check Docker status:', err);
    }
  };

  const generateBundle = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${DOCKER_API_URL}/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate bundle');
      }

      const data = await response.json();
      setBundle(data.bundle);
      setActiveStep(1);
      
      toast({
        title: 'Bundle Generated',
        description: 'Your Docker configuration is ready to download.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadAsZip = async () => {
    if (!bundle) return;

    try {
      const zip = new JSZip();
      
      for (const file of bundle.files) {
        zip.file(file.path, file.content);
      }

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'forwardarr-config.zip');
      setActiveStep(2);
      
      toast({
        title: 'Download Started',
        description: 'Your configuration bundle is downloading.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      setError('Failed to create ZIP file: ' + err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  if (!isAuthenticated) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <AlertTitle>Authentication Required</AlertTitle>
        <AlertDescription>Please log in to generate your Docker configuration.</AlertDescription>
      </Alert>
    );
  }

  return (
    <Box maxW="container.lg" mx="auto" p={4}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>
            <HStack>
              <FaDocker />
              <Text>Self-Hosted Docker Setup</Text>
            </HStack>
          </Heading>
          <Text color="gray.600">
            Run ForwardArr on your own infrastructure with encrypted API keys.
            Your indexer credentials are protected using AES-256-GCM encryption.
          </Text>
        </Box>

        {/* Status Card */}
        <Card bg={cardBg} shadow="md">
          <CardBody>
            <HStack spacing={4}>
              <Box color={status?.canGenerate ? 'green.500' : 'orange.500'}>
                <FaServer size={24} />
              </Box>
              <Box flex={1}>
                <Text fontWeight="bold">Configuration Status</Text>
                <Text fontSize="sm" color="gray.600">
                  {status?.message || 'Checking...'}
                </Text>
              </Box>
              {status?.canGenerate && (
                <Badge colorScheme="green" fontSize="sm">
                  {status.indexerCount} indexers configured
                </Badge>
              )}
            </HStack>
          </CardBody>
        </Card>

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Stepper */}
        <Card bg={cardBg} shadow="md">
          <CardBody>
            <Stepper index={activeStep} orientation="vertical" gap={0}>
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>

                  <Box flexShrink={0} ml={4} pb={6}>
                    <StepTitle>{step.title}</StepTitle>
                    <StepDescription>{step.description}</StepDescription>
                    
                    {index === 0 && activeStep === 0 && (
                      <Button
                        mt={4}
                        colorScheme="teal"
                        onClick={generateBundle}
                        isLoading={loading}
                        loadingText="Generating..."
                        isDisabled={!status?.canGenerate}
                        leftIcon={<LockIcon />}
                      >
                        Generate Secure Bundle
                      </Button>
                    )}
                    
                    {index === 1 && activeStep >= 1 && bundle && (
                      <HStack mt={4} spacing={4}>
                        <Button
                          colorScheme="teal"
                          onClick={downloadAsZip}
                          leftIcon={<DownloadIcon />}
                        >
                          Download ZIP
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setActiveStep(2)}
                        >
                          Skip to Instructions
                        </Button>
                      </HStack>
                    )}
                    
                    {index === 2 && activeStep >= 2 && (
                      <Box mt={4}>
                        <Text fontSize="sm" mb={2}>
                          Run these commands in the extracted directory:
                        </Text>
                        <Box
                          bg={codeBg}
                          p={4}
                          borderRadius="md"
                          position="relative"
                          fontFamily="mono"
                        >
                          <Code display="block" whiteSpace="pre" bg="transparent">
{`unzip forwardarr-config.zip
cd forwardarr-config
docker-compose up -d`}
                          </Code>
                          <IconButton
                            icon={<CopyIcon />}
                            size="sm"
                            position="absolute"
                            top={2}
                            right={2}
                            onClick={() => copyToClipboard('unzip forwardarr-config.zip && cd forwardarr-config && docker-compose up -d')}
                            aria-label="Copy commands"
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <StepSeparator />
                </Step>
              ))}
            </Stepper>
          </CardBody>
        </Card>

        {/* Bundle Details */}
        {bundle && (
          <Card bg={cardBg} shadow="md">
            <CardHeader>
              <Heading size="md">Bundle Contents</Heading>
            </CardHeader>
            <CardBody pt={0}>
              <List spacing={2}>
                {bundle.files.map((file) => (
                  <ListItem key={file.path}>
                    <ListIcon as={FaFileCode} color="teal.500" />
                    <Code>{file.path}</Code>
                    <Text as="span" fontSize="sm" color="gray.500" ml={2}>
                      ({file.content.length} bytes)
                    </Text>
                  </ListItem>
                ))}
              </List>
              
              <Divider my={4} />
              
              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={2}>
                  Your Proxy API Key
                </Text>
                <HStack
                  bg={codeBg}
                  p={3}
                  borderRadius="md"
                  justify="space-between"
                >
                  <Code fontSize="sm">
                    {bundle.files.find(f => f.path === 'README.md')?.content.match(/`([a-f0-9-]+)`/)?.[1] || 'See README.md'}
                  </Code>
                  <IconButton
                    icon={<CopyIcon />}
                    size="sm"
                    onClick={() => {
                      const apiKey = bundle.files.find(f => f.path === 'README.md')?.content.match(/`([a-f0-9-]+)`/)?.[1];
                      if (apiKey) copyToClipboard(apiKey);
                    }}
                    aria-label="Copy API Key"
                  />
                </HStack>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* Security Info */}
        <Alert
          status="info"
          variant="subtle"
          flexDirection="column"
          alignItems="flex-start"
          borderRadius="md"
        >
          <HStack mb={2}>
            <AlertIcon />
            <AlertTitle>Security Information</AlertTitle>
          </HStack>
          <AlertDescription>
            <List spacing={1} fontSize="sm">
              <ListItem>
                <ListIcon as={CheckCircleIcon} color="green.500" />
                Your indexer API keys are encrypted using AES-256-GCM
              </ListItem>
              <ListItem>
                <ListIcon as={CheckCircleIcon} color="green.500" />
                The encryption key is stored in your .env file - keep it secure!
              </ListItem>
              <ListItem>
                <ListIcon as={CheckCircleIcon} color="green.500" />
                Keys are decrypted only in memory at runtime
              </ListItem>
              <ListItem>
                <ListIcon as={CheckCircleIcon} color="green.500" />
                Each user gets a unique encryption key derived using HKDF
              </ListItem>
            </List>
          </AlertDescription>
        </Alert>
      </VStack>
    </Box>
  );
}

export default DockerDownload;
